import React from 'react';

// ============================================================
// TEMPLATE 1: MODERN PROFESSIONAL
// ============================================================
export const TemplateModernProfessional = ({ data }) => {
  if (!data) return null;
  const { contact, summary, skills, experience, projects, education, certifications, volunteerWork, additionalSections } = data;

  return (
    <div className="bg-white p-8 min-h-full" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Header */}
      <div className="border-b-2 border-blue-600 pb-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{contact?.name}</h1>
        <p className="text-sm text-gray-600 mt-1">
          {[contact?.email, contact?.phone, contact?.location, contact?.linkedin].filter(Boolean).join('  •  ')}
        </p>
      </div>

      {/* Summary */}
      {summary && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-2">Professional Summary</h2>
          <p className="text-sm text-gray-700 leading-relaxed">{summary}</p>
        </div>
      )}

      {/* Skills */}
      {skills?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-3">Technical Skills</h2>
          <div className="space-y-1">
            {skills.map((cat, i) => (
              <p key={i} className="text-sm">
                <span className="font-semibold text-gray-800">{cat.category}:</span>{' '}
                <span className="text-gray-600">{cat.items?.join(', ')}</span>
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Experience */}
      {experience?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-3">Professional Experience</h2>
          {experience.map((exp, i) => (
            <div key={i} className="mb-4" style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-gray-900">{exp.title}</p>
                  <p className="text-sm text-gray-600">{exp.company}{exp.location ? `, ${exp.location}` : ''}</p>
                </div>
                <p className="text-sm text-gray-500">{exp.startDate} - {exp.endDate}</p>
              </div>
              <ul className="mt-2 space-y-1">
                {exp.bullets?.map((bullet, j) => (
                  <li key={j} className="text-sm text-gray-700 flex">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Projects */}
      {projects?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-3">Projects</h2>
          {projects.map((proj, i) => (
            <div key={i} className="mb-3">
              <p className="font-semibold text-gray-900">{proj.name}</p>
              {proj.description && <p className="text-sm text-gray-600">{proj.description}</p>}
              {proj.technologies?.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">Technologies: {proj.technologies.join(', ')}</p>
              )}
              <ul className="mt-1">
                {proj.bullets?.map((bullet, j) => (
                  <li key={j} className="text-sm text-gray-700 flex">
                    <span className="text-blue-600 mr-2">•</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Education */}
      {education?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-2">Education</h2>
          {education.map((edu, i) => (
            <div key={i} className="mb-2">
              <p className="text-sm">
                <span className="font-semibold text-gray-800">{edu.degree}</span> | {edu.institution} | {edu.year}
              </p>
              {edu.details && <p className="text-sm text-gray-600">{edu.details}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Certifications */}
      {certifications?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-2">Certifications</h2>
          {certifications.map((cert, i) => (
            <p key={i} className="text-sm text-gray-700">
              • {cert.name}{cert.issuer ? ` - ${cert.issuer}` : ''}{cert.year ? ` (${cert.year})` : ''}
            </p>
          ))}
        </div>
      )}

      {/* Volunteer Work */}
      {volunteerWork?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-3">Volunteer Work</h2>
          {volunteerWork.map((vol, i) => (
            <div key={i} className="mb-3">
              <p className="font-semibold text-gray-800">{vol.role}</p>
              <p className="text-sm text-gray-600">{vol.organization}</p>
              {vol.duration && <p className="text-xs text-gray-500">{vol.duration}</p>}
              {vol.description && <p className="text-sm text-gray-700 mt-1">{vol.description}</p>}
              {vol.bullets?.map((bullet, j) => (
                <p key={j} className="text-sm text-gray-700 flex">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>{bullet}</span>
                </p>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Additional Sections */}
      {additionalSections?.map((section, i) => (
        <div key={i} className="mb-6">
          <h2 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-2">{section.title}</h2>
          {section.items?.map((item, j) => (
            <p key={j} className="text-sm text-gray-700">• {item}</p>
          ))}
        </div>
      ))}
    </div>
  );
};

// ============================================================
// TEMPLATE 2: MINIMAL ATS FRIENDLY
// ============================================================
export const TemplateMinimalATS = ({ data }) => {
  if (!data) return null;
  const { contact, summary, skills, experience, projects, education, certifications, volunteerWork, additionalSections } = data;

  return (
    <div className="bg-white p-8 min-h-full" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-xl font-bold text-gray-900 uppercase tracking-wide">{contact?.name}</h1>
        <p className="text-sm text-gray-600 mt-1">
          {[contact?.email, contact?.phone, contact?.location].filter(Boolean).join(' | ')}
        </p>
        {contact?.linkedin && <p className="text-sm text-gray-600">{contact.linkedin}</p>}
      </div>

      <hr className="border-gray-300 mb-4" />

      {/* Summary */}
      {summary && (
        <div className="mb-5">
          <h2 className="text-sm font-bold text-gray-900 uppercase mb-2">Summary</h2>
          <p className="text-sm text-gray-700">{summary}</p>
        </div>
      )}

      {/* Skills */}
      {skills?.length > 0 && (
        <div className="mb-5">
          <h2 className="text-sm font-bold text-gray-900 uppercase mb-2">Skills</h2>
          {skills.map((cat, i) => (
            <p key={i} className="text-sm text-gray-700">
              <span className="font-medium">{cat.category}:</span> {cat.items?.join(', ')}
            </p>
          ))}
        </div>
      )}

      {/* Experience */}
      {experience?.length > 0 && (
        <div className="mb-5">
          <h2 className="text-sm font-bold text-gray-900 uppercase mb-2">Experience</h2>
          {experience.map((exp, i) => (
            <div key={i} className="mb-4" style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <p className="font-medium text-gray-900">{exp.title} - {exp.company}</p>
              <p className="text-sm text-gray-600">{exp.location} | {exp.startDate} - {exp.endDate}</p>
              <ul className="mt-1 ml-4">
                {exp.bullets?.map((bullet, j) => (
                  <li key={j} className="text-sm text-gray-700 list-disc">{bullet}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Projects */}
      {projects?.length > 0 && (
        <div className="mb-5">
          <h2 className="text-sm font-bold text-gray-900 uppercase mb-2">Projects</h2>
          {projects.map((proj, i) => (
            <div key={i} className="mb-3">
              <p className="font-medium text-gray-900">{proj.name}</p>
              {proj.description && <p className="text-sm text-gray-600">{proj.description}</p>}
              {proj.technologies?.length > 0 && (
                <p className="text-xs text-gray-500">Technologies: {proj.technologies.join(', ')}</p>
              )}
              <ul className="ml-4">
                {proj.bullets?.map((bullet, j) => (
                  <li key={j} className="text-sm text-gray-700 list-disc">{bullet}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Education */}
      {education?.length > 0 && (
        <div className="mb-5">
          <h2 className="text-sm font-bold text-gray-900 uppercase mb-2">Education</h2>
          {education.map((edu, i) => (
            <div key={i} className="mb-1">
              <p className="text-sm text-gray-700">{edu.degree}, {edu.institution}, {edu.year}</p>
              {edu.details && <p className="text-sm text-gray-600">{edu.details}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Certifications */}
      {certifications?.length > 0 && (
        <div className="mb-5">
          <h2 className="text-sm font-bold text-gray-900 uppercase mb-2">Certifications</h2>
          {certifications.map((cert, i) => (
            <p key={i} className="text-sm text-gray-700">
              {cert.name}{cert.issuer ? ` - ${cert.issuer}` : ''}{cert.year ? ` (${cert.year})` : ''}
            </p>
          ))}
        </div>
      )}

      {/* Volunteer Work */}
      {volunteerWork?.length > 0 && (
        <div className="mb-5">
          <h2 className="text-sm font-bold text-gray-900 uppercase mb-2">Volunteer Work</h2>
          {volunteerWork.map((vol, i) => (
            <div key={i} className="mb-2">
              <p className="font-medium text-gray-900">{vol.role} - {vol.organization}</p>
              {vol.duration && <p className="text-xs text-gray-600">{vol.duration}</p>}
              {vol.description && <p className="text-sm text-gray-700">{vol.description}</p>}
              <ul className="ml-4">
                {vol.bullets?.map((bullet, j) => (
                  <li key={j} className="text-sm text-gray-700 list-disc">{bullet}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Additional Sections */}
      {additionalSections?.map((section, i) => (
        <div key={i} className="mb-5">
          <h2 className="text-sm font-bold text-gray-900 uppercase mb-2">{section.title}</h2>
          {section.items?.map((item, j) => (
            <p key={j} className="text-sm text-gray-700">- {item}</p>
          ))}
        </div>
      ))}
    </div>
  );
};

// ============================================================
// TEMPLATE 3: CREATIVE FORMAL
// ============================================================
export const TemplateCreativeFormal = ({ data }) => {
  if (!data) return null;
  const { contact, summary, skills, experience, projects, education, certifications, volunteerWork, additionalSections } = data;

  return (
    <div className="bg-white p-8 min-h-full" style={{ fontFamily: 'Georgia, serif' }}>
      {/* Header with accent bar */}
      <div className="relative mb-8">
        <div className="absolute left-0 top-0 w-1 h-full bg-indigo-500"></div>
        <div className="pl-6">
          <h1 className="text-3xl font-bold text-gray-900">{contact?.name}</h1>
          <p className="text-sm text-gray-600 mt-2">
            {[contact?.email, contact?.phone, contact?.location].filter(Boolean).join('  ·  ')}
          </p>
          {contact?.linkedin && <p className="text-sm text-indigo-600 mt-1">{contact.linkedin}</p>}
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="mb-6 bg-gray-50 p-4 rounded-lg border-l-4 border-indigo-500">
          <p className="text-sm text-gray-700 italic leading-relaxed">{summary}</p>
        </div>
      )}

      {/* Skills */}
      {skills?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-indigo-600 uppercase tracking-wider mb-3 flex items-center">
            <span className="w-8 h-0.5 bg-indigo-500 mr-2"></span>
            Technical Expertise
          </h2>
          <div className="grid grid-cols-2 gap-2">
            {skills.map((cat, i) => (
              <div key={i}>
                <p className="text-xs font-semibold text-gray-700 uppercase">{cat.category}</p>
                <p className="text-sm text-gray-600">{cat.items?.join(', ')}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Experience */}
      {experience?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-indigo-600 uppercase tracking-wider mb-3 flex items-center">
            <span className="w-8 h-0.5 bg-indigo-500 mr-2"></span>
            Professional Experience
          </h2>
          {experience.map((exp, i) => (
            <div key={i} className="mb-5 pl-4 border-l-2 border-gray-200">
              <div className="flex justify-between">
                <p className="font-semibold text-gray-900">{exp.title}</p>
                <p className="text-sm text-gray-500">{exp.startDate} - {exp.endDate}</p>
              </div>
              <p className="text-sm text-indigo-600">{exp.company}{exp.location ? `, ${exp.location}` : ''}</p>
              <ul className="mt-2 space-y-1">
                {exp.bullets?.map((bullet, j) => (
                  <li key={j} className="text-sm text-gray-700">◦ {bullet}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Projects */}
      {projects?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-indigo-600 uppercase tracking-wider mb-3 flex items-center">
            <span className="w-8 h-0.5 bg-indigo-500 mr-2"></span>
            Notable Projects
          </h2>
          {projects.map((proj, i) => (
            <div key={i} className="mb-3 pl-4 border-l-2 border-gray-200">
              <p className="font-semibold text-gray-900">{proj.name}</p>
              {proj.description && <p className="text-sm text-gray-600">{proj.description}</p>}
              {proj.technologies?.length > 0 && (
                <p className="text-xs text-indigo-500 mt-1">Tech: {proj.technologies.join(', ')}</p>
              )}
              <ul className="mt-1">
                {proj.bullets?.map((bullet, j) => (
                  <li key={j} className="text-sm text-gray-700">◦ {bullet}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Education & Certifications */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {education?.length > 0 && (
          <div>
            <h2 className="text-sm font-bold text-indigo-600 uppercase tracking-wider mb-2">Education</h2>
            {education.map((edu, i) => (
              <div key={i} className="mb-2">
                <p className="text-sm font-semibold text-gray-800">{edu.degree}</p>
                <p className="text-sm text-gray-600">{edu.institution}, {edu.year}</p>
                {edu.details && <p className="text-xs text-gray-500">{edu.details}</p>}
              </div>
            ))}
          </div>
        )}
        {certifications?.length > 0 && (
          <div>
            <h2 className="text-sm font-bold text-indigo-600 uppercase tracking-wider mb-2">Certifications</h2>
            {certifications.map((cert, i) => (
              <p key={i} className="text-sm text-gray-700">
                {cert.name}{cert.year ? ` (${cert.year})` : ''}
              </p>
            ))}
          </div>
        )}
      </div>

      {/* Volunteer Work */}
      {volunteerWork?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-indigo-600 uppercase tracking-wider mb-3 flex items-center">
            <span className="w-8 h-0.5 bg-indigo-500 mr-2"></span>
            Volunteer Experience
          </h2>
          {volunteerWork.map((vol, i) => (
            <div key={i} className="mb-3 pl-4 border-l-2 border-gray-200">
              <p className="font-semibold text-gray-800">{vol.role}</p>
              <p className="text-sm text-indigo-600">{vol.organization}</p>
              {vol.duration && <p className="text-xs text-gray-500">{vol.duration}</p>}
              {vol.description && <p className="text-sm text-gray-700 mt-1">{vol.description}</p>}
              <ul className="mt-1">
                {vol.bullets?.map((bullet, j) => (
                  <li key={j} className="text-sm text-gray-700">◦ {bullet}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Additional Sections */}
      {additionalSections?.map((section, i) => (
        <div key={i} className="mb-6">
          <h2 className="text-sm font-bold text-indigo-600 uppercase tracking-wider mb-2 flex items-center">
            <span className="w-8 h-0.5 bg-indigo-500 mr-2"></span>
            {section.title}
          </h2>
          {section.items?.map((item, j) => (
            <p key={j} className="text-sm text-gray-700 pl-4">◦ {item}</p>
          ))}
        </div>
      ))}
    </div>
  );
};

// ============================================================
// TEMPLATE 4: TECHNICAL ENGINEER STYLE
// ============================================================
export const TemplateTechnicalEngineer = ({ data }) => {
  if (!data) return null;
  const { contact, summary, skills, experience, projects, education, certifications, volunteerWork, additionalSections } = data;

  return (
    <div className="bg-white p-8 min-h-full" style={{ fontFamily: 'Roboto, system-ui, sans-serif' }}>
      {/* Header */}
      <div className="bg-slate-800 text-white p-6 -mx-8 -mt-8 mb-6">
        <h1 className="text-2xl font-bold">{contact?.name}</h1>
        <p className="text-slate-300 text-sm mt-1">
          {[contact?.email, contact?.phone, contact?.location].filter(Boolean).join(' | ')}
        </p>
        {contact?.linkedin && <p className="text-slate-400 text-sm mt-1">{contact.linkedin}</p>}
        {summary && <p className="text-sm text-slate-200 mt-4 leading-relaxed">{summary}</p>}
      </div>

      {/* Skills Grid */}
      {skills?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-slate-800 uppercase border-b-2 border-slate-800 pb-1 mb-3">Tech Stack</h2>
          <div className="grid grid-cols-2 gap-3">
            {skills.map((cat, i) => (
              <div key={i} className="bg-slate-50 p-3 rounded">
                <p className="text-xs font-bold text-slate-700 uppercase mb-1">{cat.category}</p>
                <div className="flex flex-wrap gap-1">
                  {cat.items?.map((skill, j) => (
                    <span key={j} className="text-xs bg-white px-2 py-0.5 rounded border border-slate-200">{skill}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Experience */}
      {experience?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-slate-800 uppercase border-b-2 border-slate-800 pb-1 mb-3">Experience</h2>
          {experience.map((exp, i) => (
            <div key={i} className="mb-4 pl-3 border-l-2 border-slate-300">
              <div className="flex justify-between">
                <p className="font-bold text-slate-800">{exp.title}</p>
                <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{exp.startDate} - {exp.endDate}</span>
              </div>
              <p className="text-sm text-slate-600">{exp.company}{exp.location ? `, ${exp.location}` : ''}</p>
              <ul className="mt-2 space-y-1">
                {exp.bullets?.map((bullet, j) => (
                  <li key={j} className="text-sm text-slate-700 flex">
                    <span className="text-slate-400 mr-2">▹</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Projects */}
      {projects?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-slate-800 uppercase border-b-2 border-slate-800 pb-1 mb-3">Projects</h2>
          <div className="grid grid-cols-2 gap-4">
            {projects.map((proj, i) => (
              <div key={i} className="bg-slate-50 p-3 rounded">
                <p className="font-semibold text-slate-800">{proj.name}</p>
                {proj.description && <p className="text-xs text-slate-600 mt-1">{proj.description}</p>}
                {proj.technologies?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {proj.technologies.map((tech, j) => (
                      <span key={j} className="text-xs bg-slate-200 px-1.5 py-0.5 rounded">{tech}</span>
                    ))}
                  </div>
                )}
                <ul className="mt-2">
                  {proj.bullets?.map((bullet, j) => (
                    <li key={j} className="text-xs text-slate-700">▹ {bullet}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Education & Certs */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {education?.length > 0 && (
          <div>
            <h2 className="text-sm font-bold text-slate-800 uppercase border-b-2 border-slate-800 pb-1 mb-2">Education</h2>
            {education.map((edu, i) => (
              <div key={i} className="mb-2">
                <p className="font-semibold text-sm text-slate-800">{edu.degree}</p>
                <p className="text-xs text-slate-600">{edu.institution}, {edu.year}</p>
                {edu.details && <p className="text-xs text-slate-500">{edu.details}</p>}
              </div>
            ))}
          </div>
        )}
        {certifications?.length > 0 && (
          <div>
            <h2 className="text-sm font-bold text-slate-800 uppercase border-b-2 border-slate-800 pb-1 mb-2">Certifications</h2>
            {certifications.map((cert, i) => (
              <p key={i} className="text-sm text-slate-700">• {cert.name}</p>
            ))}
          </div>
        )}
      </div>

      {/* Volunteer Work */}
      {volunteerWork?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-slate-800 uppercase border-b-2 border-slate-800 pb-1 mb-3">Volunteer Work</h2>
          {volunteerWork.map((vol, i) => (
            <div key={i} className="mb-3 pl-3 border-l-2 border-slate-300">
              <p className="font-bold text-slate-800">{vol.role}</p>
              <p className="text-sm text-slate-600">{vol.organization}</p>
              {vol.duration && <p className="text-xs text-slate-500">{vol.duration}</p>}
              {vol.description && <p className="text-sm text-slate-700 mt-1">{vol.description}</p>}
              <ul className="mt-1">
                {vol.bullets?.map((bullet, j) => (
                  <li key={j} className="text-sm text-slate-700">▹ {bullet}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Additional Sections */}
      {additionalSections?.map((section, i) => (
        <div key={i} className="mb-6">
          <h2 className="text-sm font-bold text-slate-800 uppercase border-b-2 border-slate-800 pb-1 mb-2">{section.title}</h2>
          {section.items?.map((item, j) => (
            <p key={j} className="text-sm text-slate-700">▹ {item}</p>
          ))}
        </div>
      ))}
    </div>
  );
};

// ============================================================
// TEMPLATE 5: CORPORATE EXECUTIVE STYLE
// ============================================================
export const TemplateCorporateExecutive = ({ data }) => {
  if (!data) return null;
  const { contact, summary, skills, experience, projects, education, certifications, volunteerWork, additionalSections } = data;

  return (
    <div className="bg-white p-10 min-h-full" style={{ fontFamily: 'Georgia, Times, serif' }}>
      {/* Elegant Header */}
      <div className="text-center mb-8 pb-6 border-b-2 border-gray-800">
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight uppercase">{contact?.name}</h1>
        <div className="flex justify-center gap-6 mt-3 text-sm text-gray-600">
          {contact?.email && <span>{contact.email}</span>}
          {contact?.phone && <span>{contact.phone}</span>}
          {contact?.location && <span>{contact.location}</span>}
        </div>
        {contact?.linkedin && <p className="text-sm text-gray-500 mt-1">{contact.linkedin}</p>}
      </div>

      {/* Executive Summary */}
      {summary && (
        <div className="mb-8 text-center max-w-xl mx-auto">
          <h2 className="text-lg font-bold text-gray-800 uppercase tracking-wider mb-3">Executive Profile</h2>
          <p className="text-gray-700 leading-relaxed">{summary}</p>
        </div>
      )}

      {/* Core Competencies */}
      {skills?.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-800 uppercase tracking-wider text-center mb-4">Core Competencies</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {skills.flatMap(cat => cat.items || []).slice(0, 15).map((skill, i) => (
              <span key={i} className="px-4 py-1.5 border border-gray-400 text-gray-700 text-sm">{skill}</span>
            ))}
          </div>
        </div>
      )}

      {/* Professional Experience */}
      {experience?.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-800 uppercase tracking-wider mb-4 pb-2 border-b border-gray-400">Professional Experience</h2>
          {experience.map((exp, i) => (
            <div key={i} className="mb-6">
              <div className="flex justify-between items-baseline mb-2">
                <div>
                  <p className="text-lg font-semibold text-gray-900">{exp.title}</p>
                  <p className="text-gray-600">{exp.company}{exp.location ? ` • ${exp.location}` : ''}</p>
                </div>
                <p className="text-sm text-gray-500 italic">{exp.startDate} – {exp.endDate}</p>
              </div>
              <ul className="space-y-2 mt-3">
                {exp.bullets?.map((bullet, j) => (
                  <li key={j} className="text-gray-700 pl-4 relative">
                    <span className="absolute left-0">◆</span>
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Projects */}
      {projects?.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-800 uppercase tracking-wider mb-4 pb-2 border-b border-gray-400">Key Projects</h2>
          {projects.map((proj, i) => (
            <div key={i} className="mb-4" style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <p className="font-semibold text-gray-900">{proj.name}</p>
              {proj.description && <p className="text-gray-600">{proj.description}</p>}
              {proj.technologies?.length > 0 && (
                <p className="text-sm text-gray-500 mt-1">Technologies: {proj.technologies.join(', ')}</p>
              )}
              <ul className="mt-2">
                {proj.bullets?.map((bullet, j) => (
                  <li key={j} className="text-gray-700 pl-4 relative">
                    <span className="absolute left-0">◆</span>
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Education & Credentials */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        {education?.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 uppercase tracking-wider mb-3 pb-2 border-b border-gray-400">Education</h2>
            {education.map((edu, i) => (
              <div key={i} className="mb-2">
                <p className="font-semibold text-gray-800">{edu.degree}</p>
                <p className="text-sm text-gray-600">{edu.institution}, {edu.year}</p>
                {edu.details && <p className="text-sm text-gray-500">{edu.details}</p>}
              </div>
            ))}
          </div>
        )}
        {certifications?.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 uppercase tracking-wider mb-3 pb-2 border-b border-gray-400">Certifications</h2>
            {certifications.map((cert, i) => (
              <p key={i} className="text-gray-700 mb-1">
                {cert.name}{cert.issuer ? ` - ${cert.issuer}` : ''}{cert.year ? ` (${cert.year})` : ''}
              </p>
            ))}
          </div>
        )}
      </div>

      {/* Volunteer Work */}
      {volunteerWork?.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-800 uppercase tracking-wider mb-4 pb-2 border-b border-gray-400">Community Involvement</h2>
          {volunteerWork.map((vol, i) => (
            <div key={i} className="mb-4" style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
              <div className="flex justify-between items-baseline">
                <p className="font-semibold text-gray-900">{vol.role}</p>
                {vol.duration && <p className="text-sm text-gray-500 italic">{vol.duration}</p>}
              </div>
              <p className="text-gray-600">{vol.organization}</p>
              {vol.description && <p className="text-gray-700 mt-1">{vol.description}</p>}
              <ul className="mt-2">
                {vol.bullets?.map((bullet, j) => (
                  <li key={j} className="text-gray-700 pl-4 relative">
                    <span className="absolute left-0">◆</span>
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Additional Sections */}
      {additionalSections?.map((section, i) => (
        <div key={i} className="mb-8">
          <h2 className="text-lg font-bold text-gray-800 uppercase tracking-wider mb-3 pb-2 border-b border-gray-400">{section.title}</h2>
          {section.items?.map((item, j) => (
            <p key={j} className="text-gray-700 pl-4 relative mb-1">
              <span className="absolute left-0">◆</span>
              {item}
            </p>
          ))}
        </div>
      ))}
    </div>
  );
};

// ============================================================
// TEMPLATE 6: TWO COLUMN MODERN
// ============================================================
export const TemplateTwoColumnModern = ({ data }) => {
  if (!data) return null;
  const { contact, summary, skills, experience, projects, education, certifications, volunteerWork, additionalSections } = data;

  return (
    <div className="bg-white min-h-full flex" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Left Sidebar */}
      <div className="w-1/3 bg-slate-700 text-white p-6">
        {/* Name */}
        <h1 className="text-xl font-bold mb-1">{contact?.name}</h1>
        
        {/* Contact */}
        <div className="mt-4 space-y-1 text-sm text-slate-300">
          {contact?.email && <p className="break-all">{contact.email}</p>}
          {contact?.phone && <p>{contact.phone}</p>}
          {contact?.location && <p>{contact.location}</p>}
          {contact?.linkedin && <p className="text-xs break-all">{contact.linkedin}</p>}
        </div>

        {/* Skills */}
        {skills?.length > 0 && (
          <div className="mt-6">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Skills</h2>
            {skills.map((cat, i) => (
              <div key={i} className="mb-3">
                <p className="text-xs font-semibold text-slate-300 uppercase">{cat.category}</p>
                <p className="text-xs text-slate-400 mt-1">{cat.items?.join(', ')}</p>
              </div>
            ))}
          </div>
        )}

        {/* Education */}
        {education?.length > 0 && (
          <div className="mt-6">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Education</h2>
            {education.map((edu, i) => (
              <div key={i} className="mb-2">
                <p className="text-sm font-semibold text-white">{edu.degree}</p>
                <p className="text-xs text-slate-400">{edu.institution}</p>
                <p className="text-xs text-slate-500">{edu.year}</p>
                {edu.details && <p className="text-xs text-slate-400 mt-1">{edu.details}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Certifications */}
        {certifications?.length > 0 && (
          <div className="mt-6">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Certifications</h2>
            {certifications.map((cert, i) => (
              <p key={i} className="text-xs text-slate-300 mb-1">{cert.name}</p>
            ))}
          </div>
        )}

        {/* Volunteer Work - Sidebar */}
        {volunteerWork?.length > 0 && (
          <div className="mt-6">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Volunteer</h2>
            {volunteerWork.map((vol, i) => (
              <div key={i} className="mb-2">
                <p className="text-xs font-semibold text-white">{vol.role}</p>
                <p className="text-xs text-slate-400">{vol.organization}</p>
              </div>
            ))}
          </div>
        )}

        {/* Additional Sections - Sidebar */}
        {additionalSections?.map((section, i) => (
          <div key={i} className="mt-6">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{section.title}</h2>
            {section.items?.map((item, j) => (
              <p key={j} className="text-xs text-slate-300 mb-1">• {item}</p>
            ))}
          </div>
        ))}
      </div>

      {/* Right Main Content */}
      <div className="w-2/3 p-6">
        {/* Summary */}
        {summary && (
          <div className="mb-6">
            <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2">About Me</h2>
            <p className="text-sm text-slate-600 leading-relaxed">{summary}</p>
          </div>
        )}

        {/* Experience */}
        {experience?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">Experience</h2>
            {experience.map((exp, i) => (
              <div key={i} className="mb-4" style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-slate-800">{exp.title}</p>
                    <p className="text-sm text-slate-500">{exp.company}{exp.location ? `, ${exp.location}` : ''}</p>
                  </div>
                  <p className="text-xs text-slate-400">{exp.startDate} - {exp.endDate}</p>
                </div>
                <ul className="mt-2 space-y-1">
                  {exp.bullets?.map((bullet, j) => (
                    <li key={j} className="text-sm text-slate-600 flex">
                      <span className="text-slate-400 mr-2">•</span>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* Projects */}
        {projects?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">Projects</h2>
            {projects.map((proj, i) => (
              <div key={i} className="mb-4" style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                <p className="font-semibold text-slate-800">{proj.name}</p>
                {proj.description && <p className="text-sm text-slate-500">{proj.description}</p>}
                {proj.technologies?.length > 0 && (
                  <p className="text-xs text-slate-400 mt-1">Tech: {proj.technologies.join(', ')}</p>
                )}
                <ul className="mt-1">
                  {proj.bullets?.map((bullet, j) => (
                    <li key={j} className="text-sm text-slate-600 flex">
                      <span className="text-slate-400 mr-2">•</span>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// TEMPLATES CONFIGURATION
// ============================================================
export const RESUME_TEMPLATES = [
  {
    id: 'modern-professional',
    name: 'Modern Professional',
    description: 'Clean sections with subtle blue accents',
    component: TemplateModernProfessional
  },
  {
    id: 'minimal-ats',
    name: 'Minimal ATS Friendly',
    description: 'Simple single column, maximum compatibility',
    component: TemplateMinimalATS
  },
  {
    id: 'creative-formal',
    name: 'Creative Formal',
    description: 'Light design elements, still professional',
    component: TemplateCreativeFormal
  },
  {
    id: 'technical-engineer',
    name: 'Technical Engineer',
    description: 'Skills and projects emphasized',
    component: TemplateTechnicalEngineer
  },
  {
    id: 'corporate-executive',
    name: 'Corporate Executive',
    description: 'Strong hierarchy and typography',
    component: TemplateCorporateExecutive
  },
  {
    id: 'two-column-modern',
    name: 'Two Column Modern',
    description: 'Skills sidebar + experience main area',
    component: TemplateTwoColumnModern
  }
];